// IWCF Kill Sheet — pure deterministic formulas (P2 of Kill Sheet Redev).
//
// Authoritative benchmark: the IWCF Surface BOP Kill Sheet reference
// supplied by the user. Every worked number on that reference must
// reproduce *exactly* from these functions — see
// __tests__/iwcf_kill_sheet_golden.test.cjs.
//
// All functions are unit-aware:
//   units: "IMP"  → Imperial (psi, ft, ppg, bbl, bbl/stk, bbl/ft, bbl/m)
//   units: "SI"   → SI       (kPa, m,  kg/m³, m³,  m³/stk,   m³/m,   m³)
//
// Gravity constants are encapsulated so callers never see them. The IWCF
// printed form template uses values like "0.00961" / "0.0098" in its label
// text, but every worked number on the reference reproduces *exactly* with
// 0.00981 (= 9.81/1000, the standard SI conversion from kg/m³·m to kPa).
// We therefore use 0.00981 for SI and 0.052 (the canonical oilfield IMP
// gravity factor) for Imperial. The discrepancy in the form template is a
// known printed-form quirk; the worked numbers are correct.
//
// All functions return RAW floating-point values. Display rounding is the
// caller's responsibility (use the helpers in lib/calc/iwcfFormat.js). This
// is important because the IWCF reference rounds individual cells at
// display while continuing internal calculations against the unrounded
// values (e.g. FCP displays "2660" but the K-value is computed against the
// raw 2659.5).
//
// Safety perimeter: this file is brand-new. The existing
// lib/calc/wellcontrol.js is NOT modified. Existing exports
// (killMudWeight, initialCirculatingPressure, etc.) remain available and
// unchanged.

// -----------------------------------------------------------------------------
// Gravity constants
// -----------------------------------------------------------------------------
export const G_FACTOR_IMP = 0.052;    // psi per ppg per ft
export const G_FACTOR_SI  = 0.00981;  // kPa per kg/m³ per m  (g = 9.81 m/s²)

export function gravityFactor(units) {
  if (units === "SI")  return G_FACTOR_SI;
  if (units === "IMP") return G_FACTOR_IMP;
  throw new Error(`gravityFactor: units must be "IMP" or "SI" — got ${units}`);
}

// -----------------------------------------------------------------------------
// Numeric guards
// -----------------------------------------------------------------------------
const _num = (v, name) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (!Number.isFinite(n)) throw new Error(`${name} must be a finite number (got ${v})`);
  return n;
};
const _pos = (v, name) => {
  const n = _num(v, name);
  if (n <= 0) throw new Error(`${name} must be > 0 (got ${n})`);
  return n;
};
const _nonNeg = (v, name) => {
  const n = _num(v, name);
  if (n < 0) throw new Error(`${name} must be ≥ 0 (got ${n})`);
  return n;
};

// -----------------------------------------------------------------------------
// Density / gradient conversions (lossless within the chosen unit system).
// gradient = density × gravityFactor(units).
// -----------------------------------------------------------------------------
export function gradientFromDensity({ density, units }) {
  const ρ = _pos(density, "density");
  return ρ * gravityFactor(units);
}

export function densityFromGradient({ gradient, units }) {
  const g = _pos(gradient, "gradient");
  return g / gravityFactor(units);
}

// -----------------------------------------------------------------------------
// Hydrostatic pressure at a TVD (used by Formation Pressure / BHP).
// P_hyd = density × gravityFactor × TVD.
// -----------------------------------------------------------------------------
export function hydrostaticAtTvd({ density, tvd, units }) {
  return _pos(density, "density") * _pos(tvd, "tvd") * gravityFactor(units);
}

// -----------------------------------------------------------------------------
// (C) Maximum allowable mud density from a formation-strength test.
// IWCF SI:  C = LOT / 0.00981 / shoeTVD + MW_at_test          (kg/m³)
// IWCF IMP: C = LOT / 0.052   / shoeTVD + MW_at_test          (ppg)
// -----------------------------------------------------------------------------
export function maxAllowableMudDensity({ lotPressure, mwAtTest, shoeTvd, units }) {
  const A = _pos(lotPressure,     "LOT pressure (A)");
  const B = _pos(mwAtTest,        "Mud density at test (B)");
  const D = _pos(shoeTvd,         "Shoe TVD");
  const g = gravityFactor(units);
  return A / g / D + B;
}

// -----------------------------------------------------------------------------
// MAASP from the formation strength test (IWCF page-1 cell).
// MAASP = (C − ρ_curr) × shoeTVD × gravityFactor
// Returns 0 (never negative) when current density already exceeds the
// max allowable — operationally that means MAASP has been violated and the
// caller should flag it; the function itself does not throw.
// -----------------------------------------------------------------------------
export function maaspFromLOT({ maxMudDensity, currentMW, shoeTvd, units }) {
  const C = _pos(maxMudDensity, "Max allowable mud density (C)");
  const ρ = _pos(currentMW,     "Current MW");
  const D = _pos(shoeTvd,       "Shoe TVD");
  const g = gravityFactor(units);
  return Math.max(0, (C - ρ) * D * g);
}

// -----------------------------------------------------------------------------
// Kill Fluid Density (KMD).
// KMD = SIDPP / TVD / gravityFactor + ρ_curr
// -----------------------------------------------------------------------------
export function killFluidDensity({ sidpp, tvd, currentMW, units }) {
  const P = _nonNeg(sidpp,    "SIDPP");
  const D = _pos(tvd,         "TVD");
  const ρ = _pos(currentMW,   "Current MW");
  const g = gravityFactor(units);
  return P / D / g + ρ;
}

// -----------------------------------------------------------------------------
// Kill Fluid Gradient (KMG).
// KMG = currentGradient + SIDPP / TVD
// Pure unit-agnostic (psi/ft + psi/ft  or  kPa/m + kPa/m).
// -----------------------------------------------------------------------------
export function killFluidGradient({ currentGradient, sidpp, tvd }) {
  const g0 = _pos(currentGradient,  "current gradient");
  const P  = _nonNeg(sidpp,         "SIDPP");
  const D  = _pos(tvd,              "TVD");
  return g0 + P / D;
}

// -----------------------------------------------------------------------------
// Initial Circulating Pressure (ICP).
// ICP = SIDPP + Dynamic Pressure Loss (Slow Pump Pressure).
// -----------------------------------------------------------------------------
export function initialCirculatingPressureIWCF({ sidpp, dynamicPressureLoss }) {
  const P = _nonNeg(sidpp,                "SIDPP");
  const S = _pos(dynamicPressureLoss,     "Dynamic Pressure Loss (SCR)");
  return P + S;
}

// -----------------------------------------------------------------------------
// Final Circulating Pressure (FCP).
// FCP = (KMG / currentGradient) × Dynamic Pressure Loss
// -----------------------------------------------------------------------------
export function finalCirculatingPressureIWCF({ killGradient, currentGradient, dynamicPressureLoss }) {
  const KG = _pos(killGradient,            "Kill Gradient");
  const CG = _pos(currentGradient,         "Current Gradient");
  const S  = _pos(dynamicPressureLoss,     "Dynamic Pressure Loss");
  return KG / CG * S;
}

// -----------------------------------------------------------------------------
// K-value = pressure drop per 100 strokes during the step-down.
// K_per_100 = (ICP − FCP) × 100 / totalStrokesToBit
// -----------------------------------------------------------------------------
export function kValuePer100Strokes({ icp, fcp, totalStrokesToBit }) {
  const I = _pos(icp,                  "ICP");
  const F = _pos(fcp,                  "FCP");
  const E = _pos(totalStrokesToBit,    "Total strokes to bit (E)");
  return (I - F) * 100 / E;
}

// -----------------------------------------------------------------------------
// Step-down schedule (ICP → FCP, linear, fixed 100-stroke intervals).
// Returns [{ strokes, pressure }] from 0 to floor(E/100)*100 plus the
// terminal step at exactly E.
// All values RAW; display rounding is the caller's responsibility.
// -----------------------------------------------------------------------------
export function stepDownSchedule({ icp, fcp, totalStrokesToBit, intervalStrokes = 100 }) {
  const I = _pos(icp,                "ICP");
  const F = _pos(fcp,                "FCP");
  const E = _pos(totalStrokesToBit,  "Total strokes to bit (E)");
  const dx = _pos(intervalStrokes,   "intervalStrokes");
  const slope = (F - I) / E;
  const rows = [];
  for (let s = 0; s <= E + 1e-9; s += dx) {
    if (s > E) break;
    rows.push({ strokes: s, pressure: I + slope * s });
  }
  // Terminal step at exactly E if not already present.
  if (rows.length === 0 || Math.abs(rows[rows.length - 1].strokes - E) > 1e-9) {
    rows.push({ strokes: E, pressure: F });
  }
  return rows;
}

// -----------------------------------------------------------------------------
// Pump strokes for a given volume.
// strokes = volume / pumpDisplacement
// -----------------------------------------------------------------------------
export function pumpStrokesForVolume({ volume, pumpDisplacement }) {
  return _pos(volume, "volume") / _pos(pumpDisplacement, "pumpDisplacement");
}

// -----------------------------------------------------------------------------
// Surface line strokes (active surface volume / pump displacement).
// -----------------------------------------------------------------------------
export function surfaceLineStrokes({ activeSurfaceVolume, pumpDisplacement }) {
  return _pos(activeSurfaceVolume,   "activeSurfaceVolume")
       / _pos(pumpDisplacement,      "pumpDisplacement");
}

// -----------------------------------------------------------------------------
// Circulation time = strokes / SPM (in minutes).
// -----------------------------------------------------------------------------
export function circulationTimeForStrokes({ strokes, spm }) {
  return _pos(strokes, "strokes") / _pos(spm, "spm");
}

// -----------------------------------------------------------------------------
// Formation Pressure / BHP (at shut-in).
// BHP = SIDPP + ρ_curr × gravityFactor × TVD
// -----------------------------------------------------------------------------
export function formationPressureFromSIDPP({ sidpp, currentMW, tvd, units }) {
  const P = _nonNeg(sidpp,    "SIDPP");
  const ρ = _pos(currentMW,   "currentMW");
  const D = _pos(tvd,         "TVD");
  return P + ρ * gravityFactor(units) * D;
}

// -----------------------------------------------------------------------------
// Fracture margin = MAASP − SICP.
// Returned signed; negative => SICP has exceeded MAASP (operationally bad).
// -----------------------------------------------------------------------------
export function fractureMargin({ maasp, sicp }) {
  return _pos(maasp, "MAASP") - _nonNeg(sicp, "SICP");
}

// -----------------------------------------------------------------------------
// Kill margin — returns both density and pressure form.
//   density:  KMD − ρ_curr             (ppg or kg/m³)
//   pressure: (KMD − ρ_curr) × g × TVD  (psi or kPa)
// -----------------------------------------------------------------------------
export function killMargin({ killMudDensity, currentMW, tvd, units }) {
  const K = _pos(killMudDensity,   "killMudDensity");
  const ρ = _pos(currentMW,        "currentMW");
  const D = _pos(tvd,              "TVD");
  const delta = K - ρ;
  return { density: delta, pressure: delta * gravityFactor(units) * D };
}

// -----------------------------------------------------------------------------
// IWCF volumes — convenience wrappers that match the page-1 IWCF table cells
// 1:1. Pipe (internal) capacities and annular capacities are precomputed by
// the caller (from BHA/Casing profiles); these helpers do the multiplication
// and the section-summation, returning RAW m³ or bbl.
// -----------------------------------------------------------------------------
export function sectionVolume({ length, capacity }) {
  return _pos(length, "length") * _pos(capacity, "capacity");
}

export function sumVolumes(volumes) {
  if (!Array.isArray(volumes) || volumes.length === 0) return 0;
  return volumes
    .filter((v) => Number.isFinite(v))
    .reduce((a, b) => a + b, 0);
}
